import React, { useEffect, useMemo, useState, useRef, createContext, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ClipboardList, Users, Calendar as CalendarIcon,
  ShoppingBag, Boxes, FlaskConical, Package as PackageIcon,
  BookOpen, Scale, Calculator, Palette, LogOut, Bell, Search, HelpCircle, ArrowLeft,
  ChevronDown, X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { getAccessMode } from "@/lib/masterOrderApi";
import { api, formatApiError } from "@/lib/api";

/* Shared Master Order shell: dark chocolate sidebar + topbar + access-mode context. */

const AccessCtx = createContext({ mode: "no_access", loading: true, refetch: () => {} });
export const useMOAccess = () => useContext(AccessCtx);
export const isWritable = (mode) => mode === "internal" || mode === "full";
export const isReadOnly = (mode) => mode === "read_only";

const NAV_GROUPS = [
  {
    title: null,
    items: [
      { id: "overview",    label: "Обзор",       Icon: LayoutDashboard, path: "/master-order" },
      { id: "orders",      label: "Заказы",      Icon: ClipboardList,   path: "/master-order/orders" },
      { id: "clients",     label: "Клиенты",     Icon: Users,           path: "/master-order/clients" },
      { id: "calendar",    label: "Календарь",   Icon: CalendarIcon,    path: "/master-order/calendar" },
      { id: "recipe-recalc", label: "Расчёты",   Icon: Scale,           path: "/master-order/recipe-recalc" },
      { id: "product-cost", label: "Себестоимость изделия", Icon: Calculator, path: "/master-order/product-cost" },
    ],
  },
  {
    title: "Работа",
    items: [
      { id: "purchases",   label: "Закупки",     Icon: ShoppingBag,     path: "/master-order/purchases" },
      { id: "production",  label: "Производство", Icon: Boxes,          path: "/master-order/production" },
      { id: "brand-studio", label: "Brand Studio", Icon: Palette,       path: "/master-order/brand-studio", placeholder: true, placeholderLabel: "в разработке" },
    ],
  },
  {
    title: "Справочники и инструменты",
    items: [
      { id: "ingredients", label: "Ингредиенты", Icon: FlaskConical,    path: "/master-order/ingredients" },
      { id: "packaging",   label: "Упаковка",    Icon: PackageIcon,     path: "/master-order/packaging-items" },
      { id: "recipes",     label: "Рецептуры",   Icon: BookOpen,        path: "/master-order/recipes" },
    ],
  },
];

function Sidebar({ active, onBack, backLabel = "Назад", onOpenSupport }) {
  const nav = useNavigate();
  return (
    <aside
      className="hidden md:flex flex-col shrink-0 sticky top-0"
      style={{ width: 232, background: "#231710", color: "#EFE6D6", height: "100vh" }}
      data-testid="mo-sidebar"
    >
      <div className="pt-5 pb-4 px-5 flex items-center gap-3">
        <button
          onClick={onBack}
          className="w-9 h-9 rounded-full grid place-items-center transition hover:bg-white/5"
          style={{ boxShadow: "inset 0 0 0 1px rgba(239,230,214,0.15)", color: "#E9DBBF" }}
          title={backLabel}
          aria-label={backLabel}
          data-testid="mo-back-to-hall"
        >
          <ArrowLeft size={15} strokeWidth={1.8} />
        </button>
        <div className="min-w-0">
          <div className="text-[10.5px] tracking-[0.32em] truncate" style={{ color: "#E9DBBF" }}>ANOKINA STUDIO</div>
          <div className="mt-1 text-[13px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#C7A574" }}>
            Мастер заказа
          </div>
        </div>
      </div>
      <div className="mx-5 h-px" style={{ background: "rgba(199,165,116,0.22)" }} />

      <nav className="px-3 py-3 space-y-4 flex-1 overflow-y-auto">
        {NAV_GROUPS.map((group, gi) => (
          <div key={gi}>
            {group.title && (
              <div className="px-3.5 text-[9.5px] tracking-[0.28em] uppercase mb-1.5" style={{ color: "rgba(239,230,214,0.4)" }}>
                {group.title}
              </div>
            )}
            <div className="space-y-0.5">
              {group.items.map(({ id, label, Icon, path, placeholder, placeholderLabel }) => {
                const isActive = active === id;
                return (
                  <button
                    key={id}
                    onClick={() => nav(path)}
                    className="w-full flex items-center gap-3 px-3.5 py-2 rounded-lg text-[13px] transition text-left"
                    style={{
                      background: isActive ? "rgba(199,165,116,0.14)" : "transparent",
                      color: isActive ? "#F7EFE0" : "rgba(239,230,214,0.7)",
                      boxShadow: isActive ? "inset 0 0 0 1px rgba(199,165,116,0.28)" : "none",
                    }}
                    data-testid={`mo-nav-${id}`}
                  >
                    <Icon size={14.5} strokeWidth={1.6} />
                    <span className="flex-1 truncate">{label}</span>
                    {placeholder && (
                      <span className="text-[9px] tracking-[0.18em] uppercase opacity-60">{placeholderLabel || "скоро"}</span>
                    )}
                    {isActive && !placeholder && (
                      <span className="w-1 h-4 rounded-full" style={{ background: "#C7A574" }} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="px-3 pb-5 pt-2">
        <button
          type="button"
          onClick={onOpenSupport}
          className="w-full rounded-xl px-3 py-3 flex items-center gap-3 text-left transition hover:bg-white/5"
          style={{ background: "rgba(199,165,116,0.10)", boxShadow: "inset 0 0 0 1px rgba(199,165,116,0.2)" }}
          data-testid="mo-help-card"
        >
          <div className="w-9 h-9 rounded-full grid place-items-center shrink-0" style={{ background: "#C7A574", color: "#231710" }}>
            <HelpCircle size={16} strokeWidth={1.8} />
          </div>
          <div className="min-w-0">
            <div className="text-[12px] leading-tight" style={{ color: "#F7EFE0" }}>Справка и поддержка</div>
            <div className="text-[10.5px] mt-0.5" style={{ color: "rgba(239,230,214,0.6)" }}>Мы рядом</div>
          </div>
        </button>
      </div>
    </aside>
  );
}

function Topbar({ user, onLogout, title, subtitle }) {
  const today = useMemo(() => {
    const d = new Date();
    const months = ["января","февраля","марта","апреля","мая","июня","июля","августа","сентября","октября","ноября","декабря"];
    const days = ["воскресенье","понедельник","вторник","среда","четверг","пятница","суббота"];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}, ${days[d.getDay()]}`;
  }, []);
  const initials = ((user?.display_name || user?.name || "О К").trim().split(/\s+/).map(s => s[0]).slice(0,2).join("") || "О").toUpperCase();
  const [menuOpen, setMenuOpen] = useState(false);
  const { mode } = useMOAccess();

  return (
    <div
      className="flex items-center gap-3 md:gap-5 px-6 md:px-8 h-[72px]"
      style={{ background: "#F7F1E7", borderBottom: "1px solid #EFE4D0" }}
      data-testid="mo-topbar"
    >
      <div className="shrink-0 min-w-0">
        <h1 className="text-[24px] leading-none truncate" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }} data-testid="mo-page-title">
          {title}
        </h1>
        {subtitle && (<div className="text-[11px] mt-1 truncate" style={{ color: "#8B6F4B" }}>{subtitle}</div>)}
      </div>

      <div className="hidden xl:flex items-center gap-2 h-9 px-3 rounded-full shrink-0"
           style={{ background: "#FFFFFF", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#7A6A55" }}
           data-testid="mo-topbar-date">
        <CalendarIcon size={14} strokeWidth={1.6} />
        <span className="text-[12.5px]">{today}</span>
      </div>

      <div className="flex-1 max-w-[560px] hidden md:block">
        <div className="flex items-center gap-2 h-10 px-4 rounded-full"
             style={{ background: "#FFFFFF", boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
          <Search size={15} strokeWidth={1.7} color="#8B6F4B" />
          <input className="flex-1 bg-transparent outline-none text-[13px] placeholder:text-[#B9A692]"
                 placeholder="Поиск по заказам, клиентам, продуктам..." data-testid="mo-topbar-search" />
        </div>
      </div>

      {isReadOnly(mode) && (
        <div className="hidden md:flex items-center gap-1.5 h-9 px-3 rounded-full text-[11px] shrink-0"
             style={{ background: "#F5EBD8", color: "#8B6F4B", boxShadow: "inset 0 0 0 1px #E1CFAA" }}
             data-testid="mo-readonly-badge">
          Режим просмотра
        </div>
      )}

      <button className="relative w-10 h-10 rounded-full grid place-items-center hover:bg-white transition shrink-0"
              style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", background: "#FFFFFF" }}
              data-testid="mo-topbar-bell" aria-label="Уведомления">
        <Bell size={16} strokeWidth={1.7} color="#4A3728" />
      </button>

      <div className="relative shrink-0">
        <button onClick={() => setMenuOpen(v => !v)}
                className="flex items-center gap-2.5 h-10 pl-1 pr-3 rounded-full hover:bg-white transition"
                style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", background: "#FFFFFF" }}
                data-testid="mo-topbar-avatar">
          <div className="w-8 h-8 rounded-full grid place-items-center text-[12px]"
               style={{ background: "linear-gradient(135deg, #EADFCF 0%, #C7A574 100%)", color: "#2A1D14",
                        fontFamily: "'Cormorant Garamond', serif", fontSize: 14 }}>
            {initials}
          </div>
          <div className="text-left hidden lg:block">
            <div className="text-[12.5px] leading-none" style={{ color: "#2A1D14" }}>
              {user?.display_name || user?.name || "Пользователь"}
            </div>
            <div className="text-[10px] mt-0.5" style={{ color: "#8B6F4B" }}>Кондитер</div>
          </div>
          <ChevronDown size={13} color="#8B6F4B" strokeWidth={1.8} />
        </button>
        {menuOpen && (
          <div className="absolute right-0 top-12 w-44 rounded-xl bg-white z-30 py-1.5"
               style={{ boxShadow: "0 12px 28px -12px rgba(58,43,31,0.24), inset 0 0 0 1px #EFE4D0" }}
               data-testid="mo-topbar-menu">
            <button onClick={onLogout}
                    className="w-full flex items-center gap-2 px-3 py-2 text-[13px] hover:bg-black/5 rounded-lg mx-1"
                    style={{ color: "#4A3728" }} data-testid="mo-topbar-logout">
              <LogOut size={14} strokeWidth={1.6} /> Выход
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------ Access-mode provider layer ---------------------
 * Access probes must NOT flip the user to "Мастер заказа недоступен" on
 * transient failures (offline, timeout, 5xx). We only accept a real
 * server-side denial (401/402/403) as a signal to close the section.
 * The last successful mode is cached in sessionStorage so remounts on
 * route change and refetches after mutations stay silent when the
 * connection blips.
 * ------------------------------------------------------------------------ */
const MO_MODE_CACHE_KEY = "anokina_mo_mode";
const readCachedMode = () => {
  try { return sessionStorage.getItem(MO_MODE_CACHE_KEY) || null; }
  catch { return null; }
};
const writeCachedMode = (m) => {
  try { if (m) sessionStorage.setItem(MO_MODE_CACHE_KEY, m); }
  catch { /* ignore quota / privacy mode */ }
};

export function MOAccessProvider({ children }) {
  const cached = readCachedMode();
  const [state, setState] = useState({
    mode: cached || "no_access",
    // If we already know the mode from an earlier probe in this session,
    // reveal content immediately and revalidate in the background — no
    // flashes of the loading block on every navigation.
    loading: !cached,
  });
  // Guard so we only ever try to start the 24-hour testing window ONCE per
  // provider mount. Server enforces atomicity, but this prevents redundant
  // POSTs (e.g. when NoAccessScreen renders during load).
  const startAttemptedRef = useRef(false);

  const load = async () => {
    setState((s) => ({ ...s, loading: !s.mode || s.mode === "no_access" }));
    try {
      let { mode } = await getAccessMode();
      // If server says no access, but the user is an accepted tester who
      // hasn't started the window yet — kick off the 24-hour window now.
      if (mode === "no_access" && !startAttemptedRef.current) {
        startAttemptedRef.current = true;
        try {
          const { data: app } = await api.get("/master-order-testing/applications/mine");
          if (app && app.status === "accepted") {
            // Server is authoritative: creates started_at/expires_at atomically.
            await api.post("/master-order-testing/start-access");
            const refetched = await getAccessMode();
            mode = refetched.mode;
          }
        } catch { /* silent — falls back to no_access */ }
      }
      writeCachedMode(mode);
      setState({ mode, loading: false });
    } catch (e) {
      const status = e?.response?.status;
      // 401/402/403 → real denial from server, honor it.
      if (status === 401 || status === 402 || status === 403) {
        setState({ mode: "no_access", loading: false });
        return;
      }
      // Everything else (network offline, timeout, 5xx) is transient.
      // Fall back to the last known good mode; do NOT drop the user to
      // the "Мастер заказа недоступен" screen mid-session.
      const fallback = readCachedMode();
      setState((s) => ({
        mode: fallback || s.mode || "no_access",
        loading: false,
      }));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  return <AccessCtx.Provider value={{ ...state, refetch: load }}>{children}</AccessCtx.Provider>;
}

export function NoAccessScreen() {
  const nav = useNavigate();
  // Fetch our own testing application so we can render a different closed
  // screen for testers whose 24-hour window already ended.
  const [application, setApplication] = useState(null);
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/master-order-testing/applications/mine");
        if (!cancelled) setApplication(data || null);
      } catch { /* anon / offline — leave null, show default */ }
      finally { if (!cancelled) setChecked(true); }
    })();
    return () => { cancelled = true; };
  }, []);

  const testCompleted = !!application && (
    application.status === "completed_test" || application.status === "feedback_received"
  );

  if (!checked) {
    // Silent placeholder — avoids flashing the wrong copy for testers.
    return <div className="min-h-[60vh]" data-testid="mo-closed-screen-loading" />;
  }

  if (testCompleted) {
    const gotoFeedback = application.status === "completed_test"
      ? () => nav("/master-order-testing/feedback")
      : () => nav("/master-order-testing");
    return (
      <div className="min-h-[60vh] grid place-items-center px-6 text-center" data-testid="mo-closed-screen">
        <div className="max-w-md">
          <div className="text-[10.5px] tracking-[0.3em] uppercase mb-2" style={{ color: "#8B6F4B" }}>
            ANOKINA STUDIO · МАСТЕР ЗАКАЗА
          </div>
          <div className="text-[28px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
            Тестирование завершено
          </div>
          <p className="text-[13px] mt-3" style={{ color: "#5B4326" }}>
            Спасибо, что познакомились с Мастером заказа. Для нас особенно важна Ваша честная обратная связь.
          </p>
          <div className="mt-6 flex flex-col items-center gap-3">
            <button
              data-testid="mo-closed-feedback-button"
              onClick={gotoFeedback}
              className="h-11 px-6 rounded-full text-[13px]"
              style={{ background: "#4A3728", color: "#F7EFE0" }}
            >
              {application.status === "completed_test" ? "Поделиться впечатлениями" : "Открыть страницу тестирования"}
            </button>
            <button
              data-testid="mo-closed-back-button"
              onClick={() => nav("/hall")}
              className="h-11 px-6 rounded-full text-[13px] bg-transparent"
              style={{ color: "#5B4326", boxShadow: "inset 0 0 0 1px #C7A97A" }}
            >
              Вернуться в Главный холл
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] grid place-items-center px-6 text-center" data-testid="mo-closed-screen">
      <div className="max-w-md">
        <div className="text-[10.5px] tracking-[0.3em] uppercase mb-2" style={{ color: "#8B6F4B" }}>
          ANOKINA STUDIO · МАСТЕР ЗАКАЗА
        </div>
        <div className="text-[28px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
          Мастер заказа готовится к открытию
        </div>
        <p className="text-[13px] mt-3" style={{ color: "#5B4326" }}>
          Сейчас мы формируем первую закрытую группу кондитеров для тестирования
          Мастера заказа до официального запуска.
        </p>
        <div className="mt-6 flex flex-col items-center gap-3">
          <button
            data-testid="mo-closed-apply-button"
            onClick={() => nav("/master-order-testing")}
            className="h-11 px-6 rounded-full text-[13px]"
            style={{ background: "#4A3728", color: "#F7EFE0" }}
          >
            Подать заявку на тестирование
          </button>
          <button
            data-testid="mo-closed-back-button"
            onClick={() => nav("/hall")}
            className="h-11 px-6 rounded-full text-[13px] bg-transparent"
            style={{ color: "#5B4326", boxShadow: "inset 0 0 0 1px #C7A97A" }}
          >
            Вернуться в Главный холл
          </button>
        </div>
      </div>
    </div>
  );
}

export function LoadingBlock({ label = "Загрузка..." }) {
  return (
    <div className="rounded-xl bg-white p-8 grid place-items-center text-[13px]"
         style={{ color: "#8B6F4B", boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
      {label}
    </div>
  );
}

export default function MOShell({ active, title, subtitle, children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [supportOpen, setSupportOpen] = useState(false);

  // Internal Master Order back navigation:
  // - from Overview (top-level of the module) the arrow exits to /hall
  // - from any inner page the arrow goes back to Overview (/master-order)
  // Exit to hall from inner pages should be a separate explicit action —
  // it must not be smuggled onto the same arrow button.
  const isOverview = active === "overview";
  const backTarget = isOverview ? "/hall" : "/master-order";
  const backLabel = isOverview ? "Назад в Anokina Studio" : "К обзору «Мастера заказа»";

  return (
    <div className="min-h-screen flex" style={{ background: "#F7F1E7" }} data-testid="mo-shell">
      <Sidebar active={active} onBack={() => nav(backTarget)} backLabel={backLabel}
               onOpenSupport={() => setSupportOpen(true)} />
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top strip */}
        <div className="md:hidden flex items-center gap-2 px-4 py-3" style={{ borderBottom: "1px solid #EFE4D0" }}>
          <button onClick={() => nav(backTarget)}
                  className="w-9 h-9 rounded-full grid place-items-center"
                  style={{ background: "#FFFFFF", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#4A3728" }}
                  data-testid="mo-mobile-back" aria-label={backLabel} title={backLabel}>
            <ArrowLeft size={15} strokeWidth={1.7} />
          </button>
          <div className="min-w-0 flex-1">
            <div className="text-[9.5px] tracking-[0.28em]" style={{ color: "#8B6F4B" }}>ANOKINA STUDIO</div>
            <div className="text-[13px] truncate" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
              {title}
            </div>
          </div>
          <button onClick={() => setSupportOpen(true)}
                  className="w-9 h-9 rounded-full grid place-items-center shrink-0"
                  style={{ background: "#FFFFFF", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#4A3728" }}
                  data-testid="mo-mobile-help" aria-label="Справка и поддержка" title="Справка и поддержка">
            <HelpCircle size={15} strokeWidth={1.7} />
          </button>
        </div>
        <div className="hidden md:block">
          <Topbar user={user} onLogout={async () => { await logout(); nav("/"); }} title={title} subtitle={subtitle} />
        </div>
        <div className="mx-auto w-full max-w-[1360px] px-4 md:px-8 py-5 md:py-6 flex-1 min-w-0 overflow-x-clip" key={location.pathname}>
          {children}
        </div>
      </main>
      {supportOpen && <SupportDrawer onClose={() => setSupportOpen(false)} />}
    </div>
  );
}

/* ─────────── Support drawer (Sprint MO-Polish) ───────────
 * Wraps the existing SupportRequest system (see /api/master-order/support-requests
 * on the backend, which delegates to POST /api/account/requests via the seeded
 * system Material). New messages appear in the user's «Мой кабинет → Мои обращения»
 * inbox with source anchor "Мастер заказа — поддержка". */
function SupportDrawer({ onClose }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    const text = (body || "").trim();
    if (!text) { toast.error("Опишите вопрос или проблему"); return; }
    if (sending) return;
    setSending(true);
    try {
      await api.post("/master-order/support-requests", { body: text });
      toast.success("Обращение отправлено");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e) || "Не удалось отправить обращение");
    } finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center px-0 md:px-4"
         style={{ background: "rgba(35,23,16,0.55)" }}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
         data-testid="mo-support-drawer">
      <div className="w-full md:max-w-md bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl flex flex-col"
           style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)", maxHeight: "92dvh" }}>
        <div className="flex-shrink-0 p-5 md:p-6 pb-2 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10.5px] tracking-[0.28em] uppercase mb-1" style={{ color: "#8B6F4B" }}>ANOKINA STUDIO</div>
            <div className="text-[20px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
              Справка и поддержка
            </div>
          </div>
          <button onClick={onClose}
                  className="w-8 h-8 rounded-full grid place-items-center shrink-0"
                  style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                  aria-label="Закрыть"
                  data-testid="mo-support-close">
            <X size={13} />
          </button>
        </div>
        <div className="p-5 md:p-6 pt-2 overflow-y-auto">
          <p className="text-[13px] mb-3" style={{ color: "#5B4326" }}>
            Опишите вопрос или проблему. Мы ответим Вам в разделе «Мои обращения».
          </p>
          <div className="text-[10.5px] tracking-[0.16em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Сообщение</div>
          <textarea value={body}
                    onChange={(e) => setBody(e.target.value.slice(0, 4000))}
                    placeholder="Например: не открывается расчёт по заказу №12."
                    rows={5}
                    className="w-full px-3 py-2 rounded-md bg-white text-base outline-none resize-y"
                    style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14", minHeight: 120 }}
                    data-testid="mo-support-body" />
          <div className="mt-4 flex items-center justify-end gap-2">
            <button onClick={onClose}
                    className="h-10 px-4 rounded-full text-[13px]"
                    style={{ background: "transparent", color: "#4A3728",
                             boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    data-testid="mo-support-cancel">
              Отмена
            </button>
            <button onClick={send} disabled={sending || !body.trim()}
                    className="h-10 px-4 rounded-full text-[13px] disabled:opacity-60"
                    style={{ background: "#4A3728", color: "#F7EFE0" }}
                    data-testid="mo-support-send">
              {sending ? "Отправляем…" : "Отправить"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { NAV_GROUPS };
